import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { rendimientoService, getRendimientoStatus } from '../../../services/rendimientoService';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend
} from 'recharts';

const KPICard = ({ icon, label, value, sub, color = 'primary' }) => (
  <div className="p-4 bg-card rounded-lg border border-border flex items-start gap-3">
    <div className={`p-2 rounded-lg bg-${color}/10 flex-shrink-0`}>
      <Icon name={icon} size={20} className={`text-${color}`} />
    </div>
    <div>
      <p className="text-xs text-muted-foreground font-caption">{label}</p>
      <p className="text-xl font-bold text-foreground font-heading">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  </div>
);

const RendimientoAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('clientes');
  const [dataClientes, setDataClientes] = useState([]);
  const [dataTipos, setDataTipos] = useState([]);
  const [evolucion, setEvolucion] = useState([]);
  const [ranking, setRanking] = useState({ mejores: [], peores: [] });
  const [mermaTotal, setMermaTotal] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [clientes, tipos, evol, rank, merma] = await Promise.all([
        rendimientoService?.getRendimientoPorCliente(),
        rendimientoService?.getRendimientoPorTipo(),
        rendimientoService?.getEvolucionMensual(),
        rendimientoService?.getRanking(),
        rendimientoService?.getMermaTotal()
      ]);
      setDataClientes(clientes);
      setDataTipos(tipos);
      setEvolucion(evol);
      setRanking(rank);
      setMermaTotal(merma);
    } catch (err) {
      setError(err?.message || 'Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'clientes', label: 'Por Cliente', icon: 'Users' },
    { id: 'tipos', label: 'Por Producto', icon: 'Package' },
    { id: 'evolucion', label: 'Evolución', icon: 'TrendingUp' },
    { id: 'ranking', label: 'Ranking', icon: 'Award' }
  ];

  const avgRendimiento = dataClientes?.length > 0
    ? (dataClientes?.reduce((s, d) => s + d?.rendimientoMedio, 0) / dataClientes?.length)?.toFixed(1)
    : '—';

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload?.map((entry, i) => (
          <p key={i} style={{ color: entry?.color }}>
            {entry?.name}: {typeof entry?.value === 'number' ? entry?.value?.toFixed(1) : entry?.value}
            {entry?.name?.includes('Rendimiento') ? '%' : entry?.name?.includes('Merma') ? ' kg' : ''}
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 bg-card rounded-lg border border-border">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="BarChart2" size={20} color="var(--color-primary)" />
          <h2 className="font-heading font-semibold text-foreground text-lg">Estadísticas de Rendimiento</h2>
        </div>
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          Cargando estadísticas...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-card rounded-lg border border-border">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="BarChart2" size={20} color="var(--color-primary)" />
          <h2 className="font-heading font-semibold text-foreground text-lg">Estadísticas de Rendimiento</h2>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={16} color="#dc2626" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadData} className="ml-auto text-sm text-primary hover:underline">Reintentar</button>
        </div>
      </div>
    );
  }

  const totalPiezas = dataClientes?.reduce((s, d) => s + d?.count, 0);

  return (
    <div className="p-4 lg:p-6 bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between mb-4 lg:mb-6">
        <div className="flex items-center gap-2">
          <Icon name="BarChart2" size={20} color="var(--color-primary)" />
          <h2 className="font-heading font-semibold text-foreground text-lg">
            Estadísticas de Rendimiento
          </h2>
        </div>
        <button onClick={loadData} className="text-xs text-primary hover:underline flex items-center gap-1">
          <Icon name="RefreshCw" size={12} />
          Actualizar
        </button>
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KPICard icon="TrendingUp" label="Rendimiento Medio" value={`${avgRendimiento}%`} sub="Todas las piezas" color="primary" />
        <KPICard icon="Scale" label="Merma Total Acumulada" value={`${mermaTotal?.toFixed(1)} kg`} sub="Total registrado" color="warning" />
        <KPICard icon="Package" label="Piezas con Datos" value={totalPiezas} sub="Con rendimiento" color="success" />
        <KPICard icon="Award" label="Mejor Rendimiento" value={ranking?.mejores?.[0] ? `${parseFloat(ranking?.mejores?.[0]?.rendimiento)?.toFixed(1)}%` : '—'} sub="Pieza destacada" color="primary" />
      </div>
      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab?.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-muted border border-border'
            }`}
          >
            <Icon name={tab?.icon} size={14} />
            {tab?.label}
          </button>
        ))}
      </div>
      {/* Tab content */}
      {(activeTab === 'clientes' || activeTab === 'tipos') && (
        <div>
          {(activeTab === 'clientes' ? dataClientes : dataTipos)?.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Icon name="BarChart2" size={36} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin datos suficientes para mostrar estadísticas</p>
              <p className="text-xs mt-1">Registra pesos en las piezas para ver análisis</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={activeTab === 'clientes' ? dataClientes : dataTipos} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="nombre" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} angle={-30} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} domain={[0, 100]} unit="%" />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Bar dataKey="rendimientoMedio" name="Rendimiento Medio" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
      {activeTab === 'evolucion' && (
        <div>
          {evolucion?.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <Icon name="TrendingUp" size={36} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin datos de evolución mensual disponibles</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={evolucion} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <YAxis yAxisId="left" domain={[0, 100]} unit="%" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} unit=" kg" />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="left" type="monotone" dataKey="rendimientoMedio" name="Rendimiento Medio %" stroke="var(--color-primary)" strokeWidth={2} dot={{ r: 4 }} />
                <Line yAxisId="right" type="monotone" dataKey="mermaTotal" name="Merma Total kg" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
      {activeTab === 'ranking' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Best */}
          <div>
            <h3 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1">
              <Icon name="TrendingUp" size={14} />
              Mejores Rendimientos
            </h3>
            {ranking?.mejores?.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {ranking?.mejores?.map((item, i) => {
                  const st = getRendimientoStatus(parseFloat(item?.rendimiento));
                  return (
                    <div key={item?.id} className={`flex items-center gap-2 p-2 rounded-lg border ${st?.bgClass}`}>
                      <span className="text-xs font-bold text-muted-foreground w-4">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">
                          {item?.productos?.nombre || 'Sin producto'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(item?.created_at)?.toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <span className={`text-sm font-bold ${st?.textClass}`}>
                        {parseFloat(item?.rendimiento)?.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* Worst */}
          <div>
            <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-1">
              <Icon name="TrendingDown" size={14} />
              Peores Rendimientos
            </h3>
            {ranking?.peores?.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin datos</p>
            ) : (
              <div className="space-y-2">
                {ranking?.peores?.map((item, i) => {
                  const st = getRendimientoStatus(parseFloat(item?.rendimiento));
                  return (
                    <div key={item?.id} className={`flex items-center gap-2 p-2 rounded-lg border ${st?.bgClass}`}>
                      <span className="text-xs font-bold text-muted-foreground w-4">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">
                          {item?.productos?.nombre || 'Sin producto'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(item?.created_at)?.toLocaleDateString('es-ES')}
                        </p>
                      </div>
                      <span className={`text-sm font-bold ${st?.textClass}`}>
                        {parseFloat(item?.rendimiento)?.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RendimientoAnalytics;
